import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Trash2, CheckCircle2, Home, ShieldCheck, Send } from "lucide-react";
import { api } from "@/lib/api";

const NAVY = "#0F2D52";
const ROYAL = "#2E5BFF";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const TIMES = ["Morning", "Afternoon", "Evening"];
const PROGRAMS = [
  "Spanish for Kids Group Classes (ages 7+)",
  "Private One-on-One Spanish Lessons",
  "Private Family Spanish Lessons",
  "Spanish Tutoring and School Support",
  "Not Sure — I Would Like a Recommendation",
];
const GOALS = ["Learn conversational Spanish", "Build confidence speaking Spanish", "Improve pronunciation",
  "Learn basic vocabulary", "Support school Spanish classes", "Connect with family or culture",
  "Prepare for travel", "Participate in a bilingual environment", "Fun and educational enrichment", "Other"];
const INTERESTS = ["Music", "Art", "Games", "Reading", "Movement", "Sports", "Technology", "Animals", "Other"];
const REFERRALS = ["Facebook", "Instagram", "Google", "Friend or Family Referral", "Oxford Library", "School",
  "Community Event", "Local Business or Organization", "Flyer", "Returning Family", "Other"];
const POLICIES = [
  "Registration does not guarantee placement in a class.",
  "Class placement may depend on the child's age, level, learning needs, and available space.",
  "Tuition must be paid before the first class unless another arrangement has been approved.",
  "A child's space is not reserved until payment is received.",
  "Group class tuition is non-refundable once the program begins.",
  "Missed group classes are generally not eligible for refunds or makeup classes.",
  "Private lessons require at least 24 hours' notice to cancel or reschedule.",
  "Private lessons canceled with less than 24 hours' notice may be charged.",
  "Parents or guardians must provide accurate emergency and medical information.",
  "Parents or guardians are responsible for updating contact information.",
  "Children must be picked up on time.",
  "Alma y Lengua reserves the right to recommend a different program or class placement when appropriate.",
];

const label = "block text-sm font-semibold mb-2";
const inputCls = "w-full bg-white rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:ring-2 focus:ring-[#2E5BFF] focus:border-transparent";

const Section = ({ n, title, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5 }}
    className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-8 mb-6"
  >
    <div className="flex items-center gap-3 mb-6">
      <span className="w-9 h-9 shrink-0 rounded-full grid place-items-center text-white font-bold text-sm" style={{ background: NAVY }}>{n}</span>
      <h2 className="text-xl md:text-2xl font-bold" style={{ color: NAVY }}>{title}</h2>
    </div>
    {children}
  </motion.section>
);

const Pills = ({ options, value, onToggle, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((opt) => {
      const active = value.includes(opt);
      return (
        <button key={opt} type="button" onClick={() => onToggle(opt)}
          className={`px-4 py-2 rounded-full text-sm font-medium border transition ${active ? "text-white border-transparent" : "bg-white text-slate-700 border-slate-300 hover:border-[#2E5BFF]"}`}
          style={active ? { background: ROYAL } : {}}>
          {opt}
        </button>
      );
    })}
  </div>
);

const Radio = ({ options, value, onChange, name }) => (
  <div className="space-y-2" data-testid={`radio-${name}`}>
    {options.map((opt) => (
      <label key={opt} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${value === opt ? "border-[#2E5BFF] bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
        <input type="radio" name={name} checked={value === opt} onChange={() => onChange(opt)} className="mt-1 accent-[#2E5BFF]" />
        <span className="text-sm text-slate-700">{opt}</span>
      </label>
    ))}
  </div>
);

const emptyChild = () => ({ first_name: "", last_name: "", age: "", dob: "", grade: "", school: "", primary_language: "", home_exposure: "" });
const emptyPickup = () => ({ name: "", relationship: "", phone: "" });

export default function Register() {
  const [p, setP] = useState({
    parent_first_name: "", parent_last_name: "", email: "", phone: "",
    address: "", city: "", state: "", zip: "", preferred_contact: "",
    primary_language_home: "", primary_language_other: "",
  });
  const [children, setChildren] = useState([emptyChild()]);
  const [pickups, setPickups] = useState([emptyPickup()]);
  const [f, setF] = useState({
    program: "", preferred_days: [], preferred_times: [], scheduling_notes: "",
    prior_experience: "", prior_experience_desc: "",
    goals: [], goals_other: "", interests: [], interests_other: "",
    learning_support: "", allergies: "", medical: "", medication: "", safety: "",
    emergency_name: "", emergency_relationship: "", emergency_phone: "",
    alt_emergency_name: "", alt_emergency_phone: "",
    pickup_id_ack: false, photo_permission: "",
    referral_source: "", referral_thank: "", referral_other: "",
    updates_pref: "", additional_info: "",
  });
  const [consents, setConsents] = useState({ accurate: false, policies: false, payment: false, authorize: false });
  const [status, setStatus] = useState("idle");
  const [showErrors, setShowErrors] = useState(false);

  const setChild = (i, k, v) => setChildren((c) => c.map((ch, j) => j === i ? { ...ch, [k]: v } : ch));
  const setPickup = (i, k, v) => setPickups((c) => c.map((ch, j) => j === i ? { ...ch, [k]: v } : ch));
  const toggle = (key, opt) => setF((s) => ({ ...s, [key]: s[key].includes(opt) ? s[key].filter((x) => x !== opt) : [...s[key], opt] }));

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email);
  const childrenValid = children.every((c) => c.first_name && c.last_name && c.age && c.dob && c.grade);
  const isValid = useMemo(() => (
    p.parent_first_name && p.parent_last_name && emailValid && p.phone &&
    childrenValid && f.program &&
    f.emergency_name && f.emergency_relationship && f.emergency_phone &&
    f.photo_permission &&
    consents.accurate && consents.policies && consents.payment && consents.authorize
  ), [p, f, children, consents, emailValid, childrenValid]);

  const submit = async (e) => {
    e.preventDefault();
    if (!isValid) { setShowErrors(true); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setStatus("sending");
    try {
      await api.post("/registrations", { ...p, ...f, children, pickups, consents });
      setStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="min-h-screen grid place-items-center px-5 py-16" style={{ background: "#F5F7FA" }}>
        <div className="max-w-xl w-full bg-white rounded-3xl shadow-xl p-8 md:p-12 text-center" data-testid="register-success">
          <div className="w-16 h-16 rounded-full grid place-items-center mx-auto mb-6 text-white" style={{ background: ROYAL }}><CheckCircle2 size={34} /></div>
          <h1 className="text-3xl font-extrabold mb-4" style={{ color: NAVY }}>Thank You for Registering!</h1>
          <p className="text-slate-600 leading-relaxed mb-4">Your registration has been received by Alma y Lengua Bilingual Learning Center. We will review your child's information and contact you to confirm the best class or private lesson option.</p>
          <p className="text-slate-600 leading-relaxed mb-4"><strong>Your child's space is not reserved until payment is received.</strong> A PayPal invoice and registration confirmation will be sent to the email address you provided. Please allow up to 24–48 business hours for a response.</p>
          <div className="rounded-2xl p-5 my-6 text-sm text-slate-600" style={{ background: "#F5F7FA" }}>
            <p className="font-bold" style={{ color: NAVY }}>Alma y Lengua Bilingual Learning Center</p>
            <p>Phone: <a href="tel:+18563443497" className="underline">856-344-3497</a></p>
            <p>Email: <a href="mailto:info@almaylengua.com" className="underline">info@almaylengua.com</a></p>
          </div>
          <Link to="/" data-testid="register-return-home" className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-semibold text-white" style={{ background: ROYAL }}>
            <Home size={18} /> Return to Home Page
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#F5F7FA" }} className="min-h-screen">
      {/* Header */}
      <div className="text-white" style={{ background: NAVY }}>
        <div className="max-w-3xl mx-auto px-5 py-10 text-center">
          <Link to="/" className="inline-flex items-center gap-2 mb-6 text-white/80 hover:text-white text-sm"><Home size={16} /> Alma y Lengua</Link>
          <h1 className="text-3xl md:text-4xl font-extrabold mb-3">Spanish for Kids Registration</h1>
          <p className="text-white/80 leading-relaxed max-w-2xl mx-auto">Help your child build confidence while learning Spanish through games, conversation, movement, music, and interactive activities in a fun and encouraging environment.</p>
        </div>
      </div>

      <form onSubmit={submit} noValidate className="max-w-3xl mx-auto px-5 py-10" data-testid="register-form">
        {showErrors && !isValid && (
          <div className="mb-6 rounded-2xl border border-red-300 bg-red-50 text-red-700 px-5 py-4 text-sm" data-testid="register-error">
            Please complete all required fields (marked *) and required consent checkboxes.
          </div>
        )}

        {/* 1. Parent */}
        <Section n="1" title="Parent or Guardian Information">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={label}>First Name *</label><input data-testid="parent-first" className={inputCls} value={p.parent_first_name} onChange={(e) => setP({ ...p, parent_first_name: e.target.value })} /></div>
            <div><label className={label}>Last Name *</label><input data-testid="parent-last" className={inputCls} value={p.parent_last_name} onChange={(e) => setP({ ...p, parent_last_name: e.target.value })} /></div>
            <div><label className={label}>Email Address *</label><input type="email" data-testid="parent-email" className={inputCls} value={p.email} onChange={(e) => setP({ ...p, email: e.target.value })} />{showErrors && !emailValid && <p className="text-xs text-red-500 mt-1">Valid email required</p>}</div>
            <div><label className={label}>Phone Number *</label><input type="tel" data-testid="parent-phone" className={inputCls} value={p.phone} onChange={(e) => setP({ ...p, phone: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className={label}>Home Address</label><input className={inputCls} value={p.address} onChange={(e) => setP({ ...p, address: e.target.value })} /></div>
            <div><label className={label}>City</label><input className={inputCls} value={p.city} onChange={(e) => setP({ ...p, city: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={label}>State</label><input className={inputCls} value={p.state} onChange={(e) => setP({ ...p, state: e.target.value })} /></div>
              <div><label className={label}>ZIP Code</label><input className={inputCls} value={p.zip} onChange={(e) => setP({ ...p, zip: e.target.value })} /></div>
            </div>
          </div>
          <div className="mt-4"><label className={label}>Preferred Contact Method</label>
            <Pills options={["Phone call", "Text message", "Email"]} value={p.preferred_contact ? [p.preferred_contact] : []} onToggle={(o) => setP({ ...p, preferred_contact: p.preferred_contact === o ? "" : o })} />
          </div>
        </Section>

        {/* 2. Children */}
        <Section n="2" title="Child Information">
          {children.map((c, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 p-5 mb-4" data-testid={`child-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold" style={{ color: NAVY }}>Child {i + 1}</h3>
                {children.length > 1 && <button type="button" onClick={() => setChildren(children.filter((_, j) => j !== i))} className="text-red-500 text-sm inline-flex items-center gap-1"><Trash2 size={14} /> Remove</button>}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={label}>First Name *</label><input className={inputCls} value={c.first_name} onChange={(e) => setChild(i, "first_name", e.target.value)} /></div>
                <div><label className={label}>Last Name *</label><input className={inputCls} value={c.last_name} onChange={(e) => setChild(i, "last_name", e.target.value)} /></div>
                <div><label className={label}>Age *</label><input type="number" min="1" className={inputCls} value={c.age} onChange={(e) => setChild(i, "age", e.target.value)} /></div>
                <div><label className={label}>Date of Birth *</label><input type="date" className={inputCls} value={c.dob} onChange={(e) => setChild(i, "dob", e.target.value)} /></div>
                <div><label className={label}>Current Grade *</label><input className={inputCls} value={c.grade} onChange={(e) => setChild(i, "grade", e.target.value)} /></div>
                <div><label className={label}>School</label><input className={inputCls} value={c.school} onChange={(e) => setChild(i, "school", e.target.value)} /></div>
                <div><label className={label}>Primary language spoken by the child</label><input className={inputCls} value={c.primary_language} onChange={(e) => setChild(i, "primary_language", e.target.value)} /></div>
                <div><label className={label}>Languages child is exposed to at home</label><input className={inputCls} value={c.home_exposure} onChange={(e) => setChild(i, "home_exposure", e.target.value)} /></div>
              </div>
            </div>
          ))}
          <button type="button" data-testid="add-child" onClick={() => setChildren([...children, emptyChild()])} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border border-[#2E5BFF] text-[#2E5BFF] hover:bg-blue-50"><Plus size={16} /> Add Another Child</button>
          <div className="mt-6"><label className={label}>Primary Language Spoken at Home</label>
            <Pills options={["English", "Spanish", "Both English and Spanish", "Other"]} value={p.primary_language_home ? [p.primary_language_home] : []} onToggle={(o) => setP({ ...p, primary_language_home: p.primary_language_home === o ? "" : o })} />
            {p.primary_language_home === "Other" && <input className={inputCls + " mt-3"} placeholder="Please specify" value={p.primary_language_other} onChange={(e) => setP({ ...p, primary_language_other: e.target.value })} />}
          </div>
        </Section>

        {/* 3. Program */}
        <Section n="3" title="Program Selection">
          <Radio name="program" options={PROGRAMS} value={f.program} onChange={(v) => setF({ ...f, program: v })} />
          <p className="text-sm text-slate-500 mt-3 italic">Recommended for siblings or families who would like to learn and practice Spanish together at home. (Private Family Spanish Lessons)</p>
        </Section>

        {/* 4. Scheduling */}
        <Section n="4" title="Scheduling Preferences">
          <label className={label}>Preferred Days</label>
          <Pills options={DAYS} value={f.preferred_days} onToggle={(o) => toggle("preferred_days", o)} testid="days" />
          <label className={label + " mt-5"}>Preferred Time</label>
          <Pills options={TIMES} value={f.preferred_times} onToggle={(o) => toggle("preferred_times", o)} testid="times" />
          <label className={label + " mt-5"}>Please share any specific scheduling needs or availability.</label>
          <textarea rows={3} className={inputCls} value={f.scheduling_notes} onChange={(e) => setF({ ...f, scheduling_notes: e.target.value })} />
        </Section>

        {/* 5. Experience */}
        <Section n="5" title="Spanish Learning Experience">
          <label className={label}>Has your child studied or been exposed to Spanish before?</label>
          <Pills options={["Yes", "No", "A little"]} value={f.prior_experience ? [f.prior_experience] : []} onToggle={(o) => setF({ ...f, prior_experience: f.prior_experience === o ? "" : o })} />
          {(f.prior_experience === "Yes" || f.prior_experience === "A little") &&
            <textarea rows={3} className={inputCls + " mt-3"} placeholder="Please describe your child's previous Spanish experience." value={f.prior_experience_desc} onChange={(e) => setF({ ...f, prior_experience_desc: e.target.value })} />}
          <label className={label + " mt-6"}>What are your goals for your child?</label>
          <Pills options={GOALS} value={f.goals} onToggle={(o) => toggle("goals", o)} />
          {f.goals.includes("Other") && <input className={inputCls + " mt-3"} placeholder="Other goal" value={f.goals_other} onChange={(e) => setF({ ...f, goals_other: e.target.value })} />}
          <label className={label + " mt-6"}>Is there anything your child especially enjoys?</label>
          <Pills options={INTERESTS} value={f.interests} onToggle={(o) => toggle("interests", o)} />
          {f.interests.includes("Other") && <input className={inputCls + " mt-3"} placeholder="Other interest" value={f.interests_other} onChange={(e) => setF({ ...f, interests_other: e.target.value })} />}
        </Section>

        {/* 6. Learning Support */}
        <Section n="6" title="Learning Support">
          <label className={label}>Does your child have any learning accommodations, communication needs, sensory needs, or other support that would help them be successful in class?</label>
          <textarea rows={4} className={inputCls} value={f.learning_support} onChange={(e) => setF({ ...f, learning_support: e.target.value })} />
          <p className="text-sm text-slate-500 mt-2 italic">This information is used only to help us create a safe, welcoming, and supportive learning experience for your child.</p>
        </Section>

        {/* 7. Medical */}
        <Section n="7" title="Medical and Safety Information">
          {[["allergies", "Does your child have any allergies?"], ["medical", "Does your child have any medical conditions we should be aware of?"], ["medication", "Does your child require any medication during class?"], ["safety", "Is there any additional safety information we should know?"]].map(([k, q]) => (
            <div key={k} className="mb-4"><label className={label}>{q}</label><textarea rows={2} className={inputCls} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></div>
          ))}
          <p className="text-sm text-slate-500 italic">Alma y Lengua does not administer medication unless a separate written agreement has been completed.</p>
        </Section>

        {/* 8. Emergency */}
        <Section n="8" title="Emergency Contact">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={label}>Full Name *</label><input data-testid="emergency-name" className={inputCls} value={f.emergency_name} onChange={(e) => setF({ ...f, emergency_name: e.target.value })} /></div>
            <div><label className={label}>Relationship to Child *</label><input className={inputCls} value={f.emergency_relationship} onChange={(e) => setF({ ...f, emergency_relationship: e.target.value })} /></div>
            <div><label className={label}>Phone Number *</label><input type="tel" className={inputCls} value={f.emergency_phone} onChange={(e) => setF({ ...f, emergency_phone: e.target.value })} /></div>
            <div />
            <div><label className={label}>Alternate Emergency Contact</label><input className={inputCls} value={f.alt_emergency_name} onChange={(e) => setF({ ...f, alt_emergency_name: e.target.value })} /></div>
            <div><label className={label}>Alternate Emergency Phone</label><input type="tel" className={inputCls} value={f.alt_emergency_phone} onChange={(e) => setF({ ...f, alt_emergency_phone: e.target.value })} /></div>
          </div>
        </Section>

        {/* 9. Pickup */}
        <Section n="9" title="Authorized Pickup">
          {pickups.map((pu, i) => (
            <div key={i} className="grid sm:grid-cols-3 gap-4 mb-4 rounded-2xl border border-slate-200 p-4">
              <div><label className={label}>Full Name</label><input className={inputCls} value={pu.name} onChange={(e) => setPickup(i, "name", e.target.value)} /></div>
              <div><label className={label}>Relationship</label><input className={inputCls} value={pu.relationship} onChange={(e) => setPickup(i, "relationship", e.target.value)} /></div>
              <div className="flex items-end gap-2">
                <div className="flex-1"><label className={label}>Phone</label><input className={inputCls} value={pu.phone} onChange={(e) => setPickup(i, "phone", e.target.value)} /></div>
                {pickups.length > 1 && <button type="button" onClick={() => setPickups(pickups.filter((_, j) => j !== i))} className="text-red-500 pb-3"><Trash2 size={16} /></button>}
              </div>
            </div>
          ))}
          <button type="button" onClick={() => setPickups([...pickups, emptyPickup()])} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border border-[#2E5BFF] text-[#2E5BFF] hover:bg-blue-50 mb-4"><Plus size={16} /> Add Authorized Adult</button>
          <label className="flex items-start gap-3 cursor-pointer"><input type="checkbox" checked={f.pickup_id_ack} onChange={(e) => setF({ ...f, pickup_id_ack: e.target.checked })} className="mt-1 accent-[#2E5BFF]" /><span className="text-sm text-slate-700">I understand that identification may be required before my child is released.</span></label>
        </Section>

        {/* 10. Photo permission */}
        <Section n="10" title="Photo and Video Permission">
          <label className={label}>May Alma y Lengua take and use photos or short videos of your child for educational updates, social media, the website, flyers, or promotional materials? *</label>
          <Radio name="photo" options={["Yes, I give permission.", "Yes, but only for group photos where my child is not individually identified.", "No, I do not give permission."]} value={f.photo_permission} onChange={(v) => setF({ ...f, photo_permission: v })} />
        </Section>

        {/* 11. Referral */}
        <Section n="11" title="How Did You Hear About Us?">
          <Pills options={REFERRALS} value={f.referral_source ? [f.referral_source] : []} onToggle={(o) => setF({ ...f, referral_source: f.referral_source === o ? "" : o })} />
          {f.referral_source === "Friend or Family Referral" && <input className={inputCls + " mt-3"} placeholder="Who may we thank for referring you?" value={f.referral_thank} onChange={(e) => setF({ ...f, referral_thank: e.target.value })} />}
          {f.referral_source === "Other" && <input className={inputCls + " mt-3"} placeholder="Please specify" value={f.referral_other} onChange={(e) => setF({ ...f, referral_other: e.target.value })} />}
        </Section>

        {/* 12. Updates */}
        <Section n="12" title="Future Program Updates">
          <label className={label}>Would you like to receive updates about future classes, camps, family programs, community events, and registration opportunities?</label>
          <Radio name="updates" options={["Yes, by email", "Yes, by text message", "Yes, by email and text", "No"]} value={f.updates_pref} onChange={(v) => setF({ ...f, updates_pref: v })} />
          <p className="text-sm text-slate-500 mt-2 italic">You may unsubscribe at any time.</p>
        </Section>

        {/* 13. Policies */}
        <Section n="13" title="Program Policies">
          <ul className="space-y-2 mb-6">
            {POLICIES.map((pol, i) => <li key={i} className="flex gap-2 text-sm text-slate-600"><span className="text-[#2E5BFF]">•</span>{pol}</li>)}
          </ul>
          <div className="space-y-3">
            {[["accurate", "I confirm that the information provided is accurate."], ["policies", "I have read and agree to the program and payment policies."], ["payment", "I understand that my child's space is not reserved until payment is received."], ["authorize", "I authorize Alma y Lengua to contact me regarding this registration."]].map(([k, t]) => (
              <label key={k} className="flex items-start gap-3 cursor-pointer" data-testid={`consent-${k}`}>
                <input type="checkbox" checked={consents[k]} onChange={(e) => setConsents({ ...consents, [k]: e.target.checked })} className="mt-1 accent-[#2E5BFF]" />
                <span className="text-sm text-slate-700 font-medium">{t} *</span>
              </label>
            ))}
          </div>
        </Section>

        {/* 14. Additional */}
        <Section n="14" title="Additional Information">
          <label className={label}>Is there anything else you would like us to know about your child or family?</label>
          <textarea rows={4} className={inputCls} value={f.additional_info} onChange={(e) => setF({ ...f, additional_info: e.target.value })} />
        </Section>

        <div className="rounded-2xl bg-blue-50 border border-blue-200 p-4 mb-6 flex gap-3">
          <ShieldCheck className="text-[#2E5BFF] shrink-0" size={20} />
          <p className="text-sm text-slate-600">Your information will be kept private and used only for registration, communication, safety, payment, and program administration purposes.</p>
        </div>

        {status === "error" && <p className="text-red-500 mb-4 text-center">Something went wrong. Please try again or call 856-344-3497.</p>}

        <button type="submit" data-testid="register-submit" disabled={status === "sending"}
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-5 font-bold text-white text-lg shadow-lg disabled:opacity-60 transition"
          style={{ background: ROYAL }}>
          <Send size={20} /> {status === "sending" ? "SUBMITTING..." : "REGISTER MY CHILD"}
        </button>
      </form>
    </div>
  );
}
